import {
  TrashIcon,
  TagIcon,
  GiftIcon,
  ReceiptPercentIcon,
  ShoppingBagIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  HashtagIcon,
  BuildingStorefrontIcon,
  CurrencyDollarIcon,
  DevicePhoneMobileIcon,
  CubeIcon,
  Squares2X2Icon,
} from '@heroicons/react/20/solid';
import {
  TWButton,
  TWModal,
  TWInput,
  TWSelect,
  ToggleSwitch,
  SegmentedToggle,
  FieldLabel,
  IconBadge,
} from '../../../components/ui';
import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import publicCouponApi from '../../../services/publicCouponApi';
import storeApi from '../../../services/storeApi';
import menuApi from '../../../services/menuApi';
import productApi from '../../../services/productApi';
import itemApi from '../../../services/itemApi';
import { PlatformTypes } from '../../../data/enums';
import { PublicCouponType, MENU_FIELD_COPY } from '../constants';
import {
  arrayToMenuIds,
  menuIdsToArray,
  menusForStore,
  menusWereSaved,
} from './coupon-menus';
import { useSelector } from 'react-redux';
import ModalDelete from '../../../components/modal-delete/modal-delete';
import PublicCouponUpsertModalSkeleton from './PublicCouponUpsertModalSkeleton';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const CAMPAIGN_TYPES = [
  {
    value: 'freeitem',
    type: PublicCouponType.SpendGetFreeItem,
    title: 'Spend & Get Free Item',
    tagline: 'Reward with a complimentary product',
    description:
      'Customer spends a minimum amount and receives a specific free item at checkout.',
    icon: GiftIcon,
    tone: 'emerald',
    accent:
      'from-emerald-500/10 via-emerald-500/5 to-transparent dark:from-emerald-400/10 dark:via-emerald-400/5',
    badgeActive: 'bg-emerald-500 text-white shadow-emerald-500/30',
  },
  {
    value: 'discount',
    type: PublicCouponType.SpendGetOff,
    title: 'Spend & Get Discount',
    tagline: 'Percent or fixed amount off',
    description:
      'Customer spends a minimum amount and receives a percent or fixed discount.',
    icon: ReceiptPercentIcon,
    tone: 'blue',
    accent:
      'from-blue-500/10 via-blue-500/5 to-transparent dark:from-sky-400/10 dark:via-sky-400/5',
    badgeActive: 'bg-blue-600 text-white shadow-blue-500/30 dark:bg-sky-500',
  },
  {
    value: 'buyxgety',
    type: PublicCouponType.BuyXGetY,
    title: 'Buy X Get Y',
    tagline: 'Bundled quantity-based offer',
    description:
      'Customer buys a set quantity of an item and receives additional units for free.',
    icon: ShoppingBagIcon,
    tone: 'violet',
    accent:
      'from-violet-500/10 via-violet-500/5 to-transparent dark:from-violet-400/10 dark:via-violet-400/5',
    badgeActive: 'bg-violet-600 text-white shadow-violet-500/30',
  },
];

const DEFAULT_ERRORS = {
  name: false,
  code: false,
  companyid: false,
  storeid: false,
  minprice: false,
  discountamount: false,
  freeitemid: false,
  getProductItemId: false,
  buyItemCount: false,
  getItemCount: false,
};

const PublicCouponUpsertModal = ({
  show,
  onClose,
  initialData,
  onSuccess,
  onError,
}) => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);

  const [form, setForm] = useState({
    id: '',
    name: '',
    code: '',
    companyid: globalCompany.id,
    storeid: null,
    type: 0,
    minprice: 0,
    discountamount: null,
    discounttype: 1, // 0 = Percent, 1 = Fixed (default)
    freeitemid: null,
    getProductItemId: null,
    buyItemCount: null,
    getItemCount: null,
    platforms: '0,10,20',
    menuids: [],
    status: 0,
  });

  const [errors, setErrors] = useState(DEFAULT_ERRORS);

  const [loading, setLoading] = useState({
    upsertLoading: false,
    deleteLoading: false,
    storeLoading: false,
    itemLoading: false,
    dataLoading: false,
    menuLoading: false,
  });

  const [storeList, setStoreList] = useState([]);
  const [menuList, setMenuList] = useState([]);
  // True once Menu/List answered for this open. Until then the form's menu
  // ids cannot be checked against the list, so they are neither shown nor
  // pruned (a slow list must not wipe an edited coupon's menus).
  const [menusLoaded, setMenusLoaded] = useState(false);
  // Menu/List failed: the form's menu ids cannot be checked, so they are kept
  // and shown as they are (a raw id is better than a false "Any menu").
  const [menusFailed, setMenusFailed] = useState(false);
  const [productList, setProductList] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [step, setStep] = useState(1); // 1: Select type, 2: Configure
  const [deletedCoupon, setDeletedCoupon] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const platformOptions = Object.entries(PlatformTypes).map(
    ([value, label]) => ({
      label,
      value: String(value),
    }),
  );

  // Fetch coupon data by ID when modal opens
  useEffect(() => {
    if (show && initialData?.id) {
      const fetchCouponData = async () => {
        try {
          setLoading((prev) => ({ ...prev, dataLoading: true }));
          const response = await publicCouponApi.getById(initialData.id);
          if (response.data?.data) {
            const data = response.data.data;
            setForm({
              id: data.id,
              name: data.name,
              code: data.code,
              companyid: data.companyid,
              storeid: data.storeid || null,
              type: data.type,
              minprice: data.minprice || 0,
              discountamount: data.discountamount || null,
              discounttype: data.discounttype ?? 1, // null means Fixed for backward compatibility
              freeitemid: data.freeitemid || null,
              getProductItemId: data.getProductItemId || null,
              buyItemCount: data.buyItemCount || null,
              getItemCount: data.getItemCount || null,
              platforms: data.platforms || '0,10,20',
              menuids: menuIdsToArray(data.menuids),
              status: data.status,
            });
            setStep(2);
            // Set selected product ID if we have getProductItemId (for BuyXGetY)
            // For SpendGetFreeItem, we'll find the product from the item when items are loaded
            if (data.getProductItemId) {
              setSelectedProductId(data.getProductItemId);
            } else if (data.freeitem?.productid) {
              setSelectedProductId(data.freeitem.productid);
            } else {
              setSelectedProductId(null);
            }
          }
        } catch (error) {
          if (onError) onError(error);
        } finally {
          setLoading((prev) => ({ ...prev, dataLoading: false }));
        }
      };
      fetchCouponData();
    } else if (show && !initialData) {
      // Reset form for new coupon
      setForm({
        id: '',
        name: '',
        code: '',
        companyid: globalCompany.id,
        storeid: null,
        type: 0,
        minprice: 0,
        discountamount: null,
        discounttype: 1, // Fixed by default
        freeitemid: null,
        getProductItemId: null,
        buyItemCount: null,
        getItemCount: null,
        platforms: '0,10,20',
        menuids: [],
        status: 0,
      });
      setStep(1);
      setSelectedProductId(null);
      setErrors(DEFAULT_ERRORS);
      setItemList([]);
      setProductList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialData?.id, globalCompany.id]);

  // Fetch store list
  useEffect(() => {
    if (show) {
      getStoreList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, globalCompany.id]);

  // Fetch the company's menus once per open; the store narrows them
  // client-side (Menu/List's storeid filter does not narrow).
  useEffect(() => {
    if (show) {
      getMenuList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, globalCompany.id]);

  const visibleMenus = menusForStore(menuList, form.storeid);

  // A store change can leave a chosen menu the new store does not carry:
  // drop it silently, or the save would hit the server's "Menu is not on this
  // store". Only menus the list KNOWS are pruned. An id absent from Menu/List
  // altogether may be a menu this user's list does not show (a 200 can be
  // incomplete or empty), so it is kept and shown as it is; dropping it would
  // widen the coupon to every menu with nothing on screen. The server
  // answers `Menu not found` if it really is gone.
  useEffect(() => {
    if (!menusLoaded) return;
    setForm((prev) => {
      const kept = prev.menuids.filter(
        (id) =>
          !menuList.some((m) => m.id === id) ||
          visibleMenus.some((m) => m.id === id),
      );
      return kept.length === prev.menuids.length
        ? prev
        : { ...prev, menuids: kept };
    });
    // form.menuids: the edit load replaces the form, and may land after the
    // list did; a prune that changes nothing returns the same state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.storeid, form.menuids, menuList, menusLoaded]);

  // Fetch products when storeid changes (for SpendGetFreeItem and BuyXGetY)
  useEffect(() => {
    if (show && form.storeid) {
      getProductList(form.storeid);
    } else if (show && !form.storeid) {
      setItemList([]);
      setProductList([]);
      setSelectedProductId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.storeid, show]);

  // Fetch items when product is selected (for SpendGetFreeItem)
  useEffect(() => {
    if (
      show &&
      form.storeid &&
      form.type === PublicCouponType.SpendGetFreeItem &&
      selectedProductId
    ) {
      getItemList(selectedProductId);
    } else if (
      show &&
      form.type === PublicCouponType.SpendGetFreeItem &&
      !selectedProductId
    ) {
      setItemList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, form.type, form.storeid, show]);

  // Auto-select item if there's only 1 item in the list
  useEffect(() => {
    if (
      show &&
      form.type === PublicCouponType.SpendGetFreeItem &&
      itemList.length === 1 &&
      form.freeitemid !== itemList[0].id
    ) {
      setForm((prev) => ({ ...prev, freeitemid: itemList[0].id }));
    }
  }, [itemList, form.type, form.freeitemid, show]);

  // When editing and we have freeitemid, we need to find the product
  // This requires fetching items for each product until we find the matching item
  useEffect(() => {
    let isMounted = true;

    if (
      show &&
      form.storeid &&
      form.type === PublicCouponType.SpendGetFreeItem &&
      form.freeitemid &&
      productList.length > 0 &&
      !selectedProductId &&
      itemList.length === 0
    ) {
      // Try to find the product by checking items
      const findProductForItem = async () => {
        for (const product of productList) {
          if (!isMounted) break;
          try {
            const response = await itemApi.list(product.id);
            if (response.data?.data?.length > 0 && isMounted) {
              const item = response.data.data.find(
                (i) => i.id === form.freeitemid,
              );
              if (item && isMounted) {
                setSelectedProductId(product.id);
                setItemList(response.data.data);
                break;
              }
            }
          } catch (error) {
            // Continue to next product
          }
        }
      };
      findProductForItem();
    }

    return () => {
      isMounted = false;
    };
  }, [
    productList,
    form.freeitemid,
    form.type,
    form.storeid,
    show,
    selectedProductId,
    itemList.length,
  ]);

  const getStoreList = async () => {
    try {
      setLoading((prev) => ({ ...prev, storeLoading: true }));
      const query = `companyid=${globalCompany.id}`;
      const list = await storeApi.list(query);
      if (list.data?.data?.length > 0) {
        const stores = list.data.data;
        setStoreList(stores);
        // Company sadece 1 tane store var ise onu secili getir
        if (stores.length === 1) {
          setForm((prev) => ({
            ...prev,
            storeid: prev.storeid || stores[0].id,
          }));
        }
        // Spend Get Free Item icin ilk store'u otomatik sec
        if (
          stores.length > 0 &&
          !form.storeid &&
          form.type === PublicCouponType.SpendGetFreeItem
        ) {
          setForm((prev) => ({
            ...prev,
            storeid: stores[0].id,
          }));
        }
      } else {
        setStoreList([]);
      }
    } catch (error) {
      if (onError) onError(error);
    } finally {
      setLoading((prev) => ({ ...prev, storeLoading: false }));
    }
  };

  const getMenuList = async () => {
    try {
      setLoading((prev) => ({ ...prev, menuLoading: true }));
      setMenusLoaded(false);
      setMenusFailed(false);
      const res = await menuApi.list(`?companyid=${globalCompany.id}`);
      setMenuList(res.data?.data || []);
      setMenusLoaded(true);
    } catch (error) {
      // The coupon can still be saved as "Any menu".
      setMenuList([]);
      setMenusFailed(true);
      if (onError) onError(error);
    } finally {
      setLoading((prev) => ({ ...prev, menuLoading: false }));
    }
  };

  const getProductList = async (storeId) => {
    if (!storeId) {
      setProductList([]);
      return;
    }
    try {
      setLoading((prev) => ({ ...prev, itemLoading: true }));
      const response = await productApi.getByStoreId(storeId);
      if (response.data?.data?.length > 0) {
        setProductList(response.data.data);
      } else {
        setProductList([]);
      }
    } catch (error) {
      if (onError) onError(error);
      setProductList([]);
    } finally {
      setLoading((prev) => ({ ...prev, itemLoading: false }));
    }
  };

  const getItemList = async (productId) => {
    if (!productId) {
      setItemList([]);
      return;
    }
    try {
      setLoading((prev) => ({ ...prev, itemLoading: true }));
      const response = await itemApi.list(productId);
      if (response.data?.data?.length > 0) {
        const items = response.data.data;
        setItemList(items);
        // If there's only 1 item, automatically select it
        if (
          items.length === 1 &&
          form.type === PublicCouponType.SpendGetFreeItem
        ) {
          setForm((prev) => ({ ...prev, freeitemid: items[0].id }));
        }
      } else {
        setItemList([]);
      }
    } catch (error) {
      if (onError) onError(error);
      setItemList([]);
    } finally {
      setLoading((prev) => ({ ...prev, itemLoading: false }));
    }
  };

  const onFormChange = (val, key) => {
    setForm((prev) => {
      const newForm = { ...prev, [key]: val };

      // Clear freeitemid and product selection when store changes
      if (key === 'storeid') {
        newForm.freeitemid = null;
        newForm.getProductItemId = null;
        setSelectedProductId(null);
      }

      return newForm;
    });
  };

  const handleProductChange = (productId) => {
    setSelectedProductId(productId);
    // Clear freeitemid when product changes (for SpendGetFreeItem)
    if (form.type === PublicCouponType.SpendGetFreeItem) {
      setForm((prev) => ({ ...prev, freeitemid: null }));
    }
    // Set getProductItemId when product changes (for BuyXGetY)
    if (form.type === PublicCouponType.BuyXGetY) {
      setForm((prev) => ({ ...prev, getProductItemId: productId }));
    }
  };

  const handlePlatformChange = (selectedPlatforms) => {
    const platformsString =
      selectedPlatforms.length > 0 ? selectedPlatforms.join(',') : '0,10,20';
    setForm((prev) => ({ ...prev, platforms: platformsString }));
  };

  const getSelectedPlatforms = () => {
    if (!form.platforms) return [];
    return form.platforms.split(',').filter((p) => p);
  };

  // Get combined coupon type value for UI display
  const getCouponTypeValue = () => {
    if (form.type === PublicCouponType.SpendGetFreeItem) return 'freeitem';
    if (form.type === PublicCouponType.BuyXGetY) return 'buyxgety';
    if (form.type === PublicCouponType.SpendGetOff) return 'discount';
    return 'freeitem';
  };

  // Handle combined coupon type change
  const handleCouponTypeChange = (value) => {
    let newType;
    let newDiscountType;

    switch (value) {
      case 'freeitem':
        newType = PublicCouponType.SpendGetFreeItem;
        newDiscountType = null;
        break;
      case 'discount':
        newType = PublicCouponType.SpendGetOff;
        newDiscountType = 0; // Default to percent
        break;
      case 'buyxgety':
        newType = PublicCouponType.BuyXGetY;
        newDiscountType = null;
        break;
      default:
        newType = PublicCouponType.SpendGetFreeItem;
        newDiscountType = null;
    }

    setForm((prev) => ({
      ...prev,
      type: newType,
      discounttype: newDiscountType,
      discountamount: null,
      freeitemid: null,
      getProductItemId: null,
      buyItemCount: null,
      getItemCount: null,
    }));
    setSelectedProductId(null);
    setItemList([]);
    setErrors((prev) => ({
      ...prev,
      discountamount: false,
      freeitemid: false,
      getProductItemId: false,
      buyItemCount: false,
      getItemCount: false,
    }));
  };

  const validateForm = () => {
    const isPercentDiscount =
      form.type === PublicCouponType.SpendGetOff && form.discounttype === 0;
    const newErrors = {
      name: !form.name,
      code: !form.code,
      companyid: !form.companyid,
      storeid: form.type === PublicCouponType.SpendGetFreeItem && !form.storeid,
      minprice: form.minprice === null || form.minprice < 0,
      discountamount:
        form.type === PublicCouponType.SpendGetOff &&
        (!form.discountamount ||
          form.discountamount <= 0 ||
          (isPercentDiscount && form.discountamount > 100)),
      freeitemid:
        form.type === PublicCouponType.SpendGetFreeItem && !form.freeitemid,
      getProductItemId:
        form.type === PublicCouponType.BuyXGetY && !form.getProductItemId,
      buyItemCount:
        form.type === PublicCouponType.BuyXGetY &&
        (!form.buyItemCount || form.buyItemCount <= 0),
      getItemCount:
        form.type === PublicCouponType.BuyXGetY &&
        (!form.getItemCount || form.getItemCount <= 0),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const buildBody = () => {
    const body = { ...form };

    // Clean up fields based on type
    if (body.type !== PublicCouponType.SpendGetOff) {
      body.discountamount = null;
      body.discounttype = null;
    }
    if (body.type !== PublicCouponType.SpendGetFreeItem) {
      body.freeitemid = null;
    }
    if (body.type !== PublicCouponType.BuyXGetY) {
      body.getProductItemId = null;
      body.buyItemCount = null;
      body.getItemCount = null;
    }

    body.menuids = arrayToMenuIds(form.menuids);

    return body;
  };

  // Create/update and Save as Draft share this. An API deployed before the
  // dashboard ignores `menuids` and would save the coupon unrestricted in
  // silence: read it back and say so.
  const saveCoupon = async (body) => {
    const res = await publicCouponApi.upsert(body);
    if (!body.menuids) return;
    const id = body.id || res?.data?.data?.id;
    if (!id) return;
    try {
      const saved = await publicCouponApi.getById(id);
      if (!menusWereSaved(body.menuids, saved?.data?.data)) {
        message.warning(MENU_FIELD_COPY.notSaved, 6);
      }
    } catch (error) {
      // The coupon is saved; the read-back is only a net.
    }
  };

  const onFormSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = buildBody();

      // In create mode, set status to Active (2)
      // In edit mode with Draft status, also activate
      if (!form.id || form.status === 0) {
        body.status = 2;
      }

      await saveCoupon(body);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      if (onError) onError(error);
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
    }
  };

  const onSaveAsDraft = async () => {
    if (!validateForm()) return;

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = buildBody();
      body.status = 0; // Draft

      await saveCoupon(body);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      if (onError) onError(error);
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
    }
  };

  const onCloseModal = () => {
    setErrors(DEFAULT_ERRORS);
    setItemList([]);
    setProductList([]);
    setSelectedProductId(null);
    onClose();
  };

  const onClickDelete = () => {
    setDeletedCoupon(form);
    setShowDeleteModal(true);
  };

  const onConfirmDelete = async () => {
    if (!deletedCoupon) return;

    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));

      const body = {
        ...deletedCoupon,
        menuids: arrayToMenuIds(deletedCoupon.menuids),
        status: 5,
      };
      await publicCouponApi.upsert(body);
      if (onSuccess) onSuccess();
      setShowDeleteModal(false);
      onClose();
    } catch (error) {
      if (onError) onError(error);
    } finally {
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const activeType = CAMPAIGN_TYPES.find((t) => t.type === form.type);
  const isEditing = Boolean(form.id);
  const canGoBack = !isEditing && step === 2;

  const renderStepIndicator = () => (
    <div className="flex items-center gap-2">
      <div
        className={classNames(
          'flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition',
          step >= 1
            ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-300'
            : 'border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400',
        )}
      >
        <span
          className={classNames(
            'flex h-5 w-5 items-center justify-center rounded-full text-[0.65rem] font-bold',
            step > 1
              ? 'bg-emerald-500 text-white'
              : step === 1
                ? 'bg-blue-600 text-white dark:bg-sky-500'
                : 'bg-slate-300 text-slate-600',
          )}
        >
          {step > 1 ? <CheckIcon className="h-3 w-3" /> : '1'}
        </span>
        <span className="hidden sm:inline">Campaign Type</span>
        <span className="sm:hidden">Type</span>
      </div>
      <div className="h-px w-6 bg-slate-200 dark:bg-slate-700" />
      <div
        className={classNames(
          'flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition',
          step === 2
            ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-300'
            : 'border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400',
        )}
      >
        <span
          className={classNames(
            'flex h-5 w-5 items-center justify-center rounded-full text-[0.65rem] font-bold',
            step === 2
              ? 'bg-blue-600 text-white dark:bg-sky-500'
              : 'bg-slate-300 text-slate-600',
          )}
        >
          2
        </span>
        <span>Configure</span>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {CAMPAIGN_TYPES.map((option) => {
          const isActive = getCouponTypeValue() === option.value;
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleCouponTypeChange(option.value)}
              className={classNames(
                'group relative flex h-full flex-col items-start overflow-hidden rounded-md border p-4 text-left transition-all duration-200',
                isActive
                  ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10 ring-2 ring-blue-500/30 dark:border-sky-400 dark:bg-slate-900 dark:ring-sky-400/30'
                  : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600',
              )}
            >
              <div
                aria-hidden
                className={classNames(
                  'pointer-events-none absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300',
                  option.accent,
                  isActive ? 'opacity-100' : 'group-hover:opacity-60',
                )}
              />
              <div className="relative flex w-full items-start justify-between gap-3">
                <IconBadge icon={Icon} tone={option.tone} size="md" />
                {isActive ? (
                  <span
                    className={classNames(
                      'inline-flex h-6 w-6 items-center justify-center rounded-full shadow',
                      option.badgeActive,
                    )}
                  >
                    <CheckIcon className="h-3.5 w-3.5" />
                  </span>
                ) : (
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-300 transition group-hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-600">
                    <CheckIcon className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-50" />
                  </span>
                )}
              </div>
              <div className="relative mt-4 space-y-1">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {option.title}
                </h4>
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                  {option.tagline}
                </p>
                <p className="pt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                  {option.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const minPriceField = (
    <div>
      <FieldLabel icon={CurrencyDollarIcon} required>
        Customer Must Spend Minimum
      </FieldLabel>
      <div className="relative mt-1.5">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm font-medium text-slate-500 dark:text-slate-400">
          $
        </span>
        <input
          type="number"
          value={form.minprice || ''}
          onChange={(e) =>
            onFormChange(parseFloat(e.target.value) || 0, 'minprice')
          }
          placeholder="0.00"
          className={classNames(
            'block h-10 w-full rounded-md border bg-white pl-7 pr-3 text-sm text-slate-900 placeholder-slate-400 transition focus:outline-none focus:ring-1 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500',
            errors.minprice
              ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500'
              : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500 dark:border-slate-600 dark:focus:border-sky-400 dark:focus:ring-sky-400',
          )}
        />
      </div>
      {errors.minprice ? (
        <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
          Min price can not be negative
        </p>
      ) : null}
    </div>
  );

  const discountField = (
    <div>
      <div className="flex items-center justify-between gap-2">
        <FieldLabel icon={ReceiptPercentIcon} required>
          Customer Discount
        </FieldLabel>
        <SegmentedToggle
          size="sm"
          value={form.discounttype}
          onChange={(v) => onFormChange(v, 'discounttype')}
          options={[
            { value: 0, label: 'Percent' },
            { value: 1, label: 'Fixed' },
          ]}
          ariaLabel="Discount type"
        />
      </div>
      <div className="relative mt-1.5">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm font-medium text-slate-500 dark:text-slate-400">
          {form.discounttype === 0 ? '%' : '$'}
        </span>
        <input
          type="number"
          value={form.discountamount || ''}
          onChange={(e) =>
            onFormChange(parseFloat(e.target.value) || null, 'discountamount')
          }
          placeholder="0"
          max={form.discounttype === 0 ? 100 : undefined}
          className={classNames(
            'block h-10 w-full rounded-md border bg-white pl-7 pr-3 text-sm text-slate-900 placeholder-slate-400 transition focus:outline-none focus:ring-1 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500',
            errors.discountamount
              ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500'
              : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500 dark:border-slate-600 dark:focus:border-sky-400 dark:focus:ring-sky-400',
          )}
        />
      </div>
      {errors.discountamount ? (
        <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
          {form.discounttype === 0
            ? 'Discount percentage is required and must be between 1-100'
            : 'Discount amount is required and must be greater than 0'}
        </p>
      ) : null}
    </div>
  );

  const renderBasicsGroup = () => (
    <section className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900 sm:p-5">
      <header className="mb-4 flex items-center gap-2">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Basics
        </h4>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Name, code, and status
        </span>
      </header>
      <div className="space-y-5">
        <TWInput
          label="Campaign Name"
          labelIcon={TagIcon}
          placeholder='E.g. "Buy one get one free deal"'
          value={form.name}
          onChange={(e) => onFormChange(e.target.value, 'name')}
          error={errors.name ? 'Name can not be empty' : ''}
          hint={!errors.name ? 'Internal reference only' : ''}
          required
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <TWInput
            label="Coupon Code"
            labelIcon={HashtagIcon}
            placeholder='E.g. "FREE30"'
            value={form.code}
            onChange={(e) =>
              onFormChange(
                e.target.value.replace(/\s/g, '').toUpperCase(),
                'code',
              )
            }
            error={errors.code ? 'Campaign code can not be empty' : ''}
            required
          />
          {form.id && (form.status === 2 || form.status === 4) ? (
            <div className="flex h-full flex-col justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-700 dark:bg-slate-800/40">
              <FieldLabel>Campaign Status</FieldLabel>
              <div className="mt-1 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p
                    className={classNames(
                      'text-sm font-semibold',
                      form.status === 2
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-rose-600 dark:text-rose-400',
                    )}
                  >
                    {form.status === 2 ? 'Active' : 'Disabled'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {form.status === 2
                      ? 'Campaign is currently active'
                      : 'Campaign is currently disabled'}
                  </p>
                </div>
                <ToggleSwitch
                  checked={form.status === 2}
                  onChange={(checked) =>
                    onFormChange(checked ? 2 : 4, 'status')
                  }
                  tone={form.status === 2 ? 'emerald' : 'rose'}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );

  const renderRulesGroup = () => (
    <section className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900 sm:p-5">
      <header className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Rules
          </h4>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Conditions for this {activeType?.title.toLowerCase() || 'campaign'}
          </span>
        </div>
        {activeType ? (
          <span
            className={classNames(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.12em]',
              activeType.tone === 'emerald' &&
                'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300',
              activeType.tone === 'blue' &&
                'bg-blue-50 text-blue-600 dark:bg-sky-900/30 dark:text-sky-300',
              activeType.tone === 'violet' &&
                'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300',
            )}
          >
            <activeType.icon className="h-3 w-3" />
            {activeType.title}
          </span>
        ) : null}
      </header>
      <div className="space-y-5">
        {form.type !== PublicCouponType.BuyXGetY ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {minPriceField}
            {form.type === PublicCouponType.SpendGetOff ? discountField : null}
          </div>
        ) : null}

        {form.type === PublicCouponType.SpendGetFreeItem ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <TWSelect
                label="Product"
                labelIcon={CubeIcon}
                placeholder="Select Product"
                value={selectedProductId}
                onChange={handleProductChange}
                loading={loading.itemLoading}
                disabled={!form.storeid || loading.itemLoading}
                hint={
                  !form.storeid ? 'Select a store first to load products' : ''
                }
                optionFilterProp="children"
              >
                {productList.map((product) => (
                  <TWSelect.Option key={product.id} value={product.id}>
                    {product.name}
                  </TWSelect.Option>
                ))}
              </TWSelect>
            </div>
            <div>
              <TWSelect
                label="Free Item"
                labelIcon={GiftIcon}
                placeholder="Select Free Item"
                value={form.freeitemid}
                onChange={(v) => onFormChange(v, 'freeitemid')}
                loading={loading.itemLoading}
                disabled={
                  !form.storeid || !selectedProductId || loading.itemLoading
                }
                error={errors.freeitemid ? 'Free item is required' : ''}
                hint={
                  !form.storeid
                    ? 'Select a store first'
                    : !selectedProductId
                      ? 'Select a product first'
                      : ''
                }
                optionFilterProp="children"
              >
                {itemList.map((item) => (
                  <TWSelect.Option key={item.id} value={item.id}>
                    {item.name}
                  </TWSelect.Option>
                ))}
              </TWSelect>
            </div>
          </div>
        ) : null}

        {form.type === PublicCouponType.BuyXGetY ? (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <TWInput
                label="Buy Count"
                type="number"
                min="1"
                placeholder="1"
                value={form.buyItemCount || ''}
                onChange={(e) =>
                  onFormChange(parseInt(e.target.value) || null, 'buyItemCount')
                }
                error={
                  errors.buyItemCount
                    ? 'Buy item count is required and must be greater than 0'
                    : ''
                }
              />
              <TWInput
                label="Free Item Count"
                type="number"
                min="1"
                placeholder="1"
                value={form.getItemCount || ''}
                onChange={(e) =>
                  onFormChange(parseInt(e.target.value) || null, 'getItemCount')
                }
                error={
                  errors.getItemCount
                    ? 'Get item count is required and must be greater than 0'
                    : ''
                }
              />
            </div>
            <TWSelect
              label="Free Product"
              labelIcon={GiftIcon}
              placeholder="Select Free Product"
              value={form.getProductItemId}
              onChange={handleProductChange}
              loading={loading.itemLoading}
              disabled={!form.storeid || loading.itemLoading}
              error={errors.getProductItemId ? 'Free product is required' : ''}
              hint={
                !form.storeid ? 'Select a store first to load products' : ''
              }
              optionFilterProp="children"
            >
              {productList.map((product) => (
                <TWSelect.Option key={product.id} value={product.id}>
                  {product.name}
                </TWSelect.Option>
              ))}
            </TWSelect>
          </>
        ) : null}
      </div>
    </section>
  );

  const renderReachGroup = () => (
    <section className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900 sm:p-5">
      <header className="mb-4 flex items-center gap-2">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Reach
        </h4>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Where customers can redeem this campaign
        </span>
      </header>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <TWSelect
          label="Store"
          labelIcon={BuildingStorefrontIcon}
          placeholder="All Stores"
          value={form.storeid}
          onChange={(v) => onFormChange(v, 'storeid')}
          loading={loading.storeLoading}
          required={form.type === PublicCouponType.SpendGetFreeItem}
          error={
            errors.storeid ? 'Store is required for this campaign type' : ''
          }
          optionFilterProp="children"
        >
          {form.type !== PublicCouponType.SpendGetFreeItem ? (
            <TWSelect.Option key="all" value={null}>
              All Stores
            </TWSelect.Option>
          ) : null}
          {storeList.map((store) => (
            <TWSelect.Option key={store.id} value={store.id}>
              {store.name}
            </TWSelect.Option>
          ))}
        </TWSelect>
        <TWSelect
          label="Platforms"
          labelIcon={DevicePhoneMobileIcon}
          mode="multiple"
          placeholder="Select Platforms"
          value={getSelectedPlatforms()}
          onChange={handlePlatformChange}
          options={platformOptions}
          showSearch={false}
          allowClear={false}
          optionFilterProp="label"
          hint="Leave empty to enable every channel"
        />
        <TWSelect
          wrapperClassName="sm:col-span-2"
          label={MENU_FIELD_COPY.label}
          labelIcon={Squares2X2Icon}
          mode="multiple"
          placeholder={
            !menusLoaded || visibleMenus.length
              ? MENU_FIELD_COPY.placeholder
              : form.storeid
                ? MENU_FIELD_COPY.noMenusStore
                : MENU_FIELD_COPY.noMenusCompany
          }
          disabled={loading.menuLoading || !visibleMenus.length}
          value={menusLoaded || menusFailed ? form.menuids : []}
          onChange={(ids) => onFormChange(ids || [], 'menuids')}
          options={visibleMenus.map((menu) => ({
            label: menu.name,
            value: menu.id,
          }))}
          loading={loading.menuLoading}
          optionFilterProp="label"
          hint={MENU_FIELD_COPY.hint}
        />
      </div>
    </section>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      {activeType ? (
        <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-800/40">
          <IconBadge icon={activeType.icon} tone={activeType.tone} size="md" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {activeType.title}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {activeType.description}
            </p>
          </div>
          {canGoBack ? (
            <TWButton
              variant="ghost"
              size="sm"
              icon={ArrowLeftIcon}
              label="Change"
              onClick={() => setStep(1)}
            />
          ) : null}
        </div>
      ) : null}
      {renderBasicsGroup()}
      {renderRulesGroup()}
      {renderReachGroup()}
    </div>
  );

  const renderFooter = () => {
    if (step === 1) {
      return (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <TWButton variant="default" label="Cancel" onClick={onCloseModal} />
          <TWButton
            variant="primary"
            label="Continue"
            iconRight={ArrowRightIcon}
            onClick={() => setStep(2)}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          {canGoBack ? (
            <TWButton
              variant="ghost"
              icon={ArrowLeftIcon}
              label="Back"
              onClick={() => setStep(1)}
              disabled={loading.upsertLoading}
            />
          ) : null}
          {isEditing ? (
            <TWButton
              variant="danger"
              icon={TrashIcon}
              label="Delete"
              disabled={loading.deleteLoading}
              loading={loading.deleteLoading}
              onClick={onClickDelete}
            />
          ) : null}
        </div>
        <div className="flex items-center gap-2 sm:justify-end">
          {isEditing ? (
            <>
              <TWButton
                variant="default"
                label="Cancel"
                disabled={loading.upsertLoading}
                onClick={onCloseModal}
              />
              <TWButton
                variant="primary"
                label="Save Changes"
                disabled={loading.upsertLoading}
                loading={loading.upsertLoading}
                onClick={onFormSubmit}
              />
            </>
          ) : (
            <>
              <TWButton
                variant="default"
                label="Save as Draft"
                disabled={loading.upsertLoading}
                onClick={onSaveAsDraft}
              />
              <TWButton
                variant="primary"
                label="Create Campaign"
                disabled={loading.upsertLoading}
                loading={loading.upsertLoading}
                onClick={onFormSubmit}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const title = isEditing
    ? 'Edit Campaign'
    : step === 1
      ? 'Choose Campaign Type'
      : 'Configure Campaign';

  const description = isEditing
    ? 'Update the configuration for this public campaign.'
    : step === 1
      ? 'Pick the kind of promotion you want your customers to enjoy.'
      : `Fine-tune how your ${activeType?.title.toLowerCase() || 'campaign'} behaves at checkout.`;

  return (
    <>
      <TWModal
        open={show}
        onClose={onCloseModal}
        size="3xl"
        showCloseButton
        title={title}
        description={description}
        footer={loading.dataLoading ? null : renderFooter()}
      >
        {loading.dataLoading ? (
          <PublicCouponUpsertModalSkeleton />
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              {renderStepIndicator()}
            </div>
            {step === 1 ? renderStep1() : renderStep2()}
          </div>
        )}
      </TWModal>

      <ModalDelete
        title="Archive Campaign"
        loading={loading.deleteLoading}
        open={showDeleteModal}
        onCloseRequest={() => setShowDeleteModal(false)}
        onOk={onConfirmDelete}
        item={deletedCoupon}
      />
    </>
  );
};

export default PublicCouponUpsertModal;
